import React, { useContext } from 'react';
import { AppContext } from 'src/components/app-context';
import { useDispatch } from 'react-redux';
import { PriceInformation } from './components/price-information';
import type { StockStatusStates } from 'src/components/stock-status/types';

import { productPriceClick } from 'src/areas/product/actions/product';
import { logRevenueEvent } from 'src/areas/product/components/price-list/price-row/utils';

import * as S from './styles';

import type { Store } from 'src/types';
import { MediumUp } from 'src/ui/block';
import {
  TrackClickScope,
  trackOfferClickWithRevenue,
} from 'src/globals/tracking/conversion';
import RatingHelper from 'src/components/rating/rating-helper';
import RatingIcon from 'src/components/rating/smiley-rating/rating-icon';
import type { RatingString } from 'src/components/rating/smiley-rating/types';

import { getSmileyRating } from 'src/components/rating/smiley-rating/utils';

type Props = {
  id?: Store['id'];
  'data-test'?: string;
  name?: Store['name'];
  hasLogo: Store['hasLogo'];
  logoUrl?: string | null;
  logoWidth: number;
  featured: Store['featured'];
  pathname: Store['pathName'];
  rating: Store['userReviewSummary']['rating'];
  numericRating?: boolean;
  reviewCount: Store['userReviewSummary']['count'];
  renderMode?: string;
  stockStatus?: StockStatusStates | null;
  price?: number | null;
  priceId?: string;
  isDeal?: boolean;
  productId?: number;
  clickTrackingScope?: TrackClickScope;
};

export const ShopLinkCard = (props: Props) => {
  const {
    id,
    'data-test': dataTest,
    name,
    hasLogo,
    logoUrl,
    logoWidth,
    featured,
    pathname,
    rating,
    numericRating = false,
    reviewCount,
    renderMode,
    stockStatus,
    price,
    productId,
    priceId,
    isDeal = false,
    // Use this as default since this component originally was refactored out
    // from src/components/user-review-card
    clickTrackingScope = TrackClickScope.USER_REVIEW_CARD,
  } = props;

  const { t } = useContext(AppContext);
  const dispatch = useDispatch();

  const count = reviewCount && reviewCount > 500 ? '500+' : reviewCount;

  const highResolutionLogo = logoUrl
    ? { url: logoUrl, width: logoWidth }
    : null;
  const featuredStore = Boolean(featured);

  const rate = RatingHelper.convert(rating || 0);
  const smileyRating = getSmileyRating(rate.real);
  const ratingType = numericRating
    ? rate.rounded
    : t('reviews', smileyRating?.text as RatingString);

  const trackClickOut = () => {
    if (!priceId || !productId) {
      return;
    }

    // Track revenue and event on click out
    logRevenueEvent(String(priceId), productId);
    dispatch(
      productPriceClick(productId, price, featuredStore, '', false, 'StoreCard')
    );

    trackOfferClickWithRevenue({
      scope: clickTrackingScope,
      offerId: priceId,
      productId,
    });
  };

  const renderContent = () => (
    <>
      {id && name && (
        <S.Logo
          storeId={id}
          name={name}
          hasImage={Boolean(hasLogo)}
          featured={featuredStore}
          logoUrl={highResolutionLogo?.url}
          width={highResolutionLogo?.width}
          showHighResolutionLogo={Boolean(highResolutionLogo)}
        />
      )}
      {rating &&
      rating !== 0 &&
      smileyRating?.iconType &&
      smileyRating?.text ? (
        <>
          <RatingIcon
            type={smileyRating.iconType}
            disabled={!featuredStore}
            ariaLabel={ratingType.toString()}
          />
          {numericRating ? (
            <MediumUp>
              <S.RatingText>{ratingType}</S.RatingText>
            </MediumUp>
          ) : (
            <S.RatingText>{ratingType}</S.RatingText>
          )}
          {price ? (
            <MediumUp>
              <S.Count>({count})</S.Count>
            </MediumUp>
          ) : (
            <S.Count>({count})</S.Count>
          )}
        </>
      ) : null}
      {price && (
        <PriceInformation
          price={price}
          stockStatus={stockStatus}
          isDeal={isDeal}
          featured={featuredStore}
        />
      )}
    </>
  );

  if (!pathname) {
    return <S.UnfeaturedStore>{renderContent()}</S.UnfeaturedStore>;
  }

  return (
    <S.StoreLink
      data-test={dataTest}
      onClick={trackClickOut}
      to={pathname}
      isExternal={renderMode === 'native'}
      target="_blank"
    >
      {renderContent()}
    </S.StoreLink>
  );
};
